import { CURRENT_CALENDAR_WEEK_KEY, TRAILER_RADAR_CONFIG_NAME } from '@/components/trailer-radar/trailer-radar-helper';
import { getDateString } from '@/helpers/date-helper';
import { EntityHelper } from '@/helpers/entity-helper';
import { TrafficHelper } from '@/helpers/traffic-helper';
import { NewResponse } from '@/interfaces/responses/new';
import { TitleListName } from '@/interfaces/responses/title-list';
import { DailySpecials } from '@/interfaces/specials';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';

const state = () => ({
	specials: {} as Record<string, DailySpecials>,
	initialTitleLists: {} as any,
});

type State = ReturnType<typeof state>;

const getters: GetterTree<State, any> = {
	// active dates according to the loaded days in the new timeline.
	// this is used to better calculate when a special appear according to their `duration` field.
	dates: (state, getters, rootState): string[] => {
		return (rootState.title.lists.new as NewResponse).days
			.map(date => date.date)
			.filter((item, index, array) => array.indexOf(item) === index);
	},
	specials: (state, getters, rootState) => {
		return Object.keys(state.specials).reduce((specials, date) => {
			if (TrafficHelper.isSeoTraffic()) {
				return {};
			}
			const platform = 'web';
			const eligibleItems = state.specials[date].items
				.filter(item => item.platform_enabled[platform]) // @todo for mobile
				.filter(item => !item.survey_id || !(item.survey_id in rootState.user.seenSurveys)) // seen survey special
				.filter(item => (rootState.user.settings.hidden_timeline_specials || []).indexOf(item.name) === -1) // remove specials that are hidden
				// trailer radar
				.filter(item => {
					return item.name.indexOf(TRAILER_RADAR_CONFIG_NAME) !== -1
						? (rootState.user.settings.hidden_timeline_specials || []).indexOf(
								CURRENT_CALENDAR_WEEK_KEY
						  ) === -1
						: true;
				})
				.filter(item => {
					// remove trailer radar specials for the current calendar week that already have been voted for
					if (item.name.indexOf(TRAILER_RADAR_CONFIG_NAME) !== -1) {
						if (rootState.user.usedTrailerRadarTitles[CURRENT_CALENDAR_WEEK_KEY]) {
							// as soon as one of the titles has been voted on -> do not show trailer radar
							return (
								rootState.user.usedTrailerRadarTitles[CURRENT_CALENDAR_WEEK_KEY].length > 0 &&
								!rootState.user.usedTrailerRadarTitles[CURRENT_CALENDAR_WEEK_KEY].some(
									(entityId: any) => {
										const title = EntityHelper.splitEntityId(entityId)!;
										const key = `${title.objectType}_${title.objectId}`;

										return (
											(TitleListName.LIKELIST in state.initialTitleLists &&
												state.initialTitleLists[TitleListName.LIKELIST][key]) ||
											(TitleListName.DISLIKELIST in state.initialTitleLists &&
												state.initialTitleLists[TitleListName.DISLIKELIST][key])
										);
									}
								)
							);
						}
					}
					return true;
				});

			// if any items have duration: push the whole DailySpecial forward
			const duration = Math.max(...eligibleItems.map(item => item.duration || 0));
			const from = new Date(date);
			const till = new Date(date);
			till.setDate(new Date(date).getDate() + duration);
			till.getTime();

			const targetDateRaw = getters.dates
				.sort()
				.map((d: string) => new Date(d))
				.reduce((root: Date, item: Date) => {
					return item >= from && item <= till ? item : root;
					// check if current special day is in the range of date and date + duration
					// and then take the youngest date that exist on the timeline dates.
				}, new Date(date));
			const targetDate = getDateString(targetDateRaw);

			if (eligibleItems.length > 0 && targetDate) {
				specials[targetDate] = {
					...state.specials[date],
					items: eligibleItems,
				};
			}
			return specials;
		}, {} as Record<string, DailySpecials>);
	},
	dailySpecial: (state, getters) => (date: string) => {
		return getters.specials[date];
	},
};

const actions: ActionTree<State, any> = {
	// why we need the first initial title lists:
	// through reactivity the trailer radar will be updated immediately when a user likes or dislikes a title in it.
	// this would result in immediate removal of the trailer radar.
	// with the initial state we give the user the possibility to continue liking/disliking titles.
	// in a new session, the trailer radar is then removed - or when the user clicks "hide".
	setTitleLists: ({ state, commit }, titleListState: any) => {
		if (Object.keys(state.initialTitleLists).length === 0) {
			commit('SET_INITIAL_TITLE_LIST_STATE', titleListState);
		}
	},
};

const mutations: MutationTree<State> = {
	SET_SPECIALS(state, specials: Record<string, DailySpecials>) {
		state.specials = specials;
	},
	SET_INITIAL_TITLE_LIST_STATE(state, titleListState: any) {
		state.initialTitleLists = {
			[TitleListName.LIKELIST]: titleListState[TitleListName.LIKELIST],
			[TitleListName.DISLIKELIST]: titleListState[TitleListName.DISLIKELIST],
		};
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
} as Module<State, any>;
